/*global Liferay*/
import ClayIcon from "@clayui/icon";
import { ClayTooltipProvider } from "@clayui/tooltip";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../../index.scss";
import {
	enrollToLearningPath,
	getUserEnrollmentByLearningPathId,
	updateCompletedSteps,
} from "../../services/enrollment";
import { getLearningPathById } from "../../services/learning-path";
import { getLearningPathStepById } from "../../services/learning-path-step";
import "../../styles/LearningPathSteps.scss";
import {
	convertMinutesToDuration,
	getCourseFirstLessonFriendlyUrlPath,
	getPersonas,
	getTooltipPersona,
	isSignedIn,
} from "../../utils/util";
import { RELATIONSHIP, OBJECT_ENTRY_URL_SEPARATOR } from "../../utils/constants";

const LearningPathSteps = forwardRef((props, ref) => {
  const { learningPathId } = props;
  const [learningPathSteps, setLearningPathSteps] = useState(null);
  const [learningPath, setLearningPath] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [userEnrollmentId, setUserEnrollmentId] = useState(null);
  const [completedStepsIds, setCompletedStepsIds] = useState("");

	const { t } = useTranslation();

	useEffect(() => {
		if (!learningPathId) return;

		const fetchLearningPathData = async () => {
			try {
				const learningPathResponse = await getLearningPathById(learningPathId);

				const stepsData = (
					await Promise.all(
						learningPathResponse[
							RELATIONSHIP.LEARNING_PATHS_TO_LEARNING_PATHS_STEPS
						].map(async (step) => {
							const stepIsCourse = step.type?.key === "course";

							const stepType = stepIsCourse
								? RELATIONSHIP.COURSES_TO_LEARNING_PATH_STEPS
								: RELATIONSHIP.EXTERNAL_MEDIA_TO_LEARNING_PATH_STEPS;

							try {
								const stepResponse = await getLearningPathStepById(
									step.id,
									stepType
								);

								const linkedEntity = stepResponse?.[stepType];

								if (!linkedEntity) return null;

								const url = stepIsCourse
									? await getCourseFirstLessonFriendlyUrlPath(linkedEntity.id)
									: linkedEntity.id;

								if (url == null) return null;

								return {
									...step,
									totalDuration: linkedEntity.durationMinutes ?? 0,
									url,
								};
							} catch (stepError) {
								console.warn("Skipping inaccessible step", step.id, stepError);
								return null;
							}
						})
					)
				).filter(Boolean);

				setLearningPathSteps(stepsData);
				setLearningPath(learningPathResponse);
			} catch (error) {
				console.error("Error fetching learning path:", error);
			}
		};

		fetchLearningPathData();
	}, [learningPathId]);

	const totalDurationLearningPath = learningPathSteps?.reduce((acc, step) => {
		return acc + parseFloat(convertMinutesToDuration(step.totalDuration));
	}, 0);

	useEffect(() => {
		if (learningPathId) {
			const fetchUserEnrollment = async () => {
				const result = await getUserEnrollmentByLearningPathId(learningPathId);

				setUserEnrollmentId(
					result?.items?.length > 0 ? result.items[0].id : ""
				);

				setCompletedStepsIds(
					result?.items?.length > 0 ? result.items[0].completedStepsIds : []
				);
			};

			fetchUserEnrollment();
		}
	}, [learningPathId]);

	useEffect(() => {
		if (userEnrollmentId) {
			setIsEnrolled(true);
		}
	}, [userEnrollmentId, learningPathId]);

	const navigateToStepContent = (step) => {
		const url = `/${OBJECT_ENTRY_URL_SEPARATOR.LEARNING_PATH}/${step.url}`;

		window.location.href =
			step.type.key !== "course"
				? url + `?learning-path=${learningPath.id}`
				: `/course/${step.url}`;
	};

	const handleRegisterAndCompleteStep = async (step) => {
		const stepId = step.id;

		if (step.type.key === "video") {
			if (isSignedIn() && !isEnrolled) {
				await enrollToLearningPath(learningPathId, stepId);
			} else {
				const isStepCompleted = completedStepsIds?.includes(stepId);

				if (isEnrolled && !isStepCompleted) {
					await updateCompletedSteps(
						userEnrollmentId,
						completedStepsIds,
						stepId
					);
				}
			}
		}

		navigateToStepContent(step);
	};

	const handleStepClick = async (step) => {
		handleRegisterAndCompleteStep(step);
	};

	return (
		<>
			<div className="learning-path-banner d-flex justify-content-between">
				<div className="lp-banner-contents p-4">
					<div className="learning-path-tag">
						<p>{t("learning-path")}</p>
					</div>

					<h1>{learningPath?.name}</h1>

					<div className="lp-banner-duration-persona">
						<div className="lp-banner-info-tag d-flex mt-3">
							<p className="lp-banner-duration">
								{[totalDurationLearningPath, " hours"]}
							</p>
						</div>

						<div className="lp-banner-info-tag d-flex mt-3">
							{learningPath && (
								<ClayTooltipProvider>
									<div
										className="lp-banner-persona"
										data-tool-tip-align="top"
										title={getTooltipPersona(learningPath.persona)}
									>
										<p></p>
										{getPersonas(learningPath.persona)}
									</div>
								</ClayTooltipProvider>
							)}
						</div>
					</div>
				</div>
			</div>

			<p className="lp-banner-description">{learningPath?.description}</p>

			{learningPathSteps?.map((step, index) => {
				return (
					<div key={index}>
						<div
							className={`align-items-center card-banner d-flex ${step.type.key}`}
						>
							<span>Step {index + 1}</span>
						</div>
						<div
							className="learning-path-step"
							onClick={() => handleStepClick(step)}
						>
							<div
								className="col col-lg-12 col-sm-12 col-12 col-md-12 d-flex flex-column learn-education-card step-card"
								key={step.id}
							>
								<h3>{step.title}</h3>
								<div>{step.description}</div>
								<div className="d-flex card-tags">
									<div className="banner-tags card-tag d-flex mt-3">
										<div className="banner-tag card-tag__time">
											<p>
												{convertMinutesToDuration(step.totalDuration, "hours")}
											</p>
										</div>
									</div>
									<div className="ml-2">
										{completedStepsIds?.includes(step.id) ? (
											<p className="align-self-start completed-tag">
												Completed
												<ClayIcon className="ml-2" symbol="check" />
											</p>
										) : (
											""
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				);
			}) || <p>No learning path available.</p>}
		</>
	);
});

export default LearningPathSteps;